import Button from "components/button";
import ConfirmationModal from "components/shared/confirmation-modal";
import Icon from "components/icon";
import Input from "components/input";
import Select from "components/select";
import { Table } from "components/table";
import Pagination from "components/table/pagination";
import Typography from "components/typography";

import useVendorList, {
  VendorRow,
} from "./hooks/useVendorList";
import { useState, useRef, useEffect } from "react";

const VendorPage = () => {
  const {
    columns,
    currentPage,
    deleteRow,
    paginatedRows,
    rowsPerPage,
    searchValue,
    selectedKeys,
    setCurrentPage,
    setRowsPerPage,
    setSearchValue,
    setSelectedKeys,
    setShowDeleteConfirmModal,
    setSortState,
    setStatusFilter,
    showDeleteConfirmModal,
    sortState,
    statusFilter,
    t,
    isLoading,
    totalPages,
    totalSelected,
    onConfirmDelete,
    allowedBulkActions = ["Delete"],
    onBulkStatusUpdate = () => {},
    onRowClick,
  } = useVendorList();

  const [isBulkOpen, setIsBulkOpen] = useState(false);
  const [stagedBulkAction, setStagedBulkAction] = useState<string | null>(null);
  const bulkRef = useRef<HTMLDivElement>(null);
  const effectiveStagedBulkAction = totalSelected === 0 ? null : stagedBulkAction;

  useEffect(() => {
    if (!isBulkOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (bulkRef.current && !bulkRef.current.contains(e.target as Node)) {
        setIsBulkOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isBulkOpen]);

  const getBulkActionLabel = (action: string | null) => {
    if (!action) return t("btn-bulk-actions") || "Bulk Actions";
    if (action === "Delete") return t("bulk-action-delete") || "Delete";
    return action;
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4 h-[40px]">
          {totalSelected > 0 && (
            <div className="flex items-center gap-3 animate-in fade-in slide-in-from-left-2 duration-200">
              <div className="relative" ref={bulkRef}>
                <Button
                  type="button"
                  variant="outline"
                  color="tertiary"
                  size="medium"
                  iconRight={<Icon name={isBulkOpen ? "chevron-up" : "chevron-down"} size={16} />}
                  onClick={() => setIsBulkOpen(!isBulkOpen)}
                >
                  {getBulkActionLabel(effectiveStagedBulkAction)}
                </Button>

                {isBulkOpen && (
                  <div className="absolute left-0 top-full mt-1 z-50 min-w-[200px] rounded-xl border border-border bg-white py-1 shadow-lg">
                    {allowedBulkActions.map((action) => (
                      <button
                        key={action}
                        type="button"
                        onClick={() => {
                          setStagedBulkAction(action);
                          setIsBulkOpen(false);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-text-primary hover:bg-greyScale-90 transition-colors"
                      >
                        {action === "Delete" ? (t("bulk-action-delete") || "Delete") : action}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <Button
                type="button"
                variant="fill"
                color="secondary"
                size="medium"
                disabled={!stagedBulkAction}
                onClick={() => stagedBulkAction && onBulkStatusUpdate(stagedBulkAction)}
              >
                {t("btn-apply-data")}
              </Button>
            </div>
          )}
          
          <Typography
            variant="bodySmall"
            tone="muted"
          >
            {t(
              "selected-count",
              { count: totalSelected },
            )}
          </Typography>
        </div>
      </div>
      <div className="max-w-md">
        <Input
          value={searchValue}
          onChange={(
            event,
          ) => {
            setSearchValue(
              event.target.value,
            );

            setCurrentPage(1);
          }}
          placeholder={t(
            "search-placeholder",
          )}
          leftIcon={
            <Icon
              name="search"
              size={14}
            />
          }
          reserveHelperSpace={
            false
          }
        />
      </div>

      <section className="rounded-2xl border border-border bg-white shadow-sm">
        <Table<VendorRow>
          data={paginatedRows}
          loading={isLoading}
          columns={columns}
          rowKey="id"
          currentPage={
            currentPage
          }
          limit={rowsPerPage}
          selectable
          onRowClick={onRowClick}
          selectedRowKeys={
            selectedKeys
          }
          onSelectedRowKeysChange={(
            keys,
          ) =>
            setSelectedKeys(
              keys,
            )
          }
          sortState={sortState}
          onSortStateChange={
            setSortState
          }
          selectableLabel="All"
        />

        <div className="flex items-center justify-between border-t border-border px-6 py-4">
          <div className="flex items-center gap-2">
            <Typography
              variant="body"
              tone="muted"
              as="span"
            >
              {t(
                "rows-per-page",
              )}
            </Typography>

            <select
              value={
                rowsPerPage
              }
              onChange={(
                event,
              ) => {
                setRowsPerPage(
                  Number(
                    event.target
                      .value,
                  ),
                );

                setCurrentPage(1);
              }}
              className="cursor-pointer rounded-lg border border-border bg-white px-2 py-1 text-sm"
            >
              <option value={10}>
                10
              </option>

              <option value={25}>
                25
              </option>

              <option value={50}>
                50
              </option>
            </select>
          </div>

          <Pagination
            currentPage={
              currentPage
            }
            totalPages={
              totalPages
            }
            onPageChange={
              setCurrentPage
            }
          />
        </div>
      </section>

      <ConfirmationModal
        open={
          showDeleteConfirmModal
        }
        title={t(
          "modal-delete-title",
        )}
        message={t(
          "modal-delete-message",
          {
            name:
              deleteRow?.vendorName ??
              "-",
          },
        )}
        confirmLabel={t(
          "modal-ok-delete",
        )}
        cancelLabel={t(
          "btn-cancel",
        )}
        onConfirm={
          onConfirmDelete
        }
        onCancel={() =>
          setShowDeleteConfirmModal(
            false,
          )
        }
      />
    </div>
  );
};

export default VendorPage;